var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { HawkinsPropsOverrideContext, useClasses, useLayoutProps, useProps, } from '@hawkins/helpers';
import { useNavigationContext } from '../hooks';
import { useNavigationUserControlsStyles } from './navigationUserControls.styles';
var displayName = 'NavigationUserControls';
/**
 * `NavigationUserControls` contains user controls within a navigation component.
 */
export var NavigationUserControls = React.forwardRef(function (_a, ref) {
    var _b, _c, _d, _e, _f;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _g = useProps(displayName, props), actions = _g.actions, classesProp = _g.classes, className = _g.className, _h = _g.component, Component = _h === void 0 ? 'div' : _h, layout = _g.layout, styleProp = _g.style, userMenu = _g.userMenu, restProps = __rest(_g, ["actions", "classes", "className", "component", "layout", "style", "userMenu"]);
    // Context
    var _j = useNavigationContext(), isCollapsedArea = _j.isCollapsedArea, isTransparent = _j.isTransparent, orientation = _j.orientation;
    var isVertical = orientation === 'vertical';
    // Styles
    var baseClasses = useNavigationUserControlsStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var actionsButtonGroupHorizontalClassName = classes.actionsButtonGroupHorizontal;
    var actionsContainerClassName = clsx((_b = {},
        _b[classes.actionsContainerVerticalExpanded] = isVertical && !isCollapsedArea,
        _b));
    var iconButtonDisabledClassName = classes.iconButtonDisabled;
    var iconButtonIconClassName = clsx(classes.icon, (_c = {}, _c[classes.iconTransparent] = isTransparent, _c));
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_d = {},
        _d[classes.rootVerticalCollapsed] = isVertical && isCollapsedArea,
        _d[classes.rootVerticalExpanded] = isVertical && !isCollapsedArea,
        _d));
    var userMenuCaretClassName = clsx(classes.userMenuCaret, (_e = {},
        _e[classes.userMenuCaretTransparent] = isTransparent,
        _e[classes.userMenuCaretVerticalCollapsed] = isVertical && isCollapsedArea,
        _e));
    var userMenuContainerClassName = clsx((_f = {},
        _f[classes.userMenuContainerHorizontal] = !isVertical,
        _f[classes.userMenuContainerVerticalCollapsed] = isVertical && isCollapsedArea,
        _f[classes.userMenuContainerVerticalExpanded] = isVertical && !isCollapsedArea,
        _f));
    // Content
    var actionsOverrideProps = React.useMemo(function () { return ({
        Button: {
            classes: { icon: iconButtonIconClassName, rootDisabled: iconButtonDisabledClassName },
            compact: true,
            tooltipPlacement: isVertical ? (isCollapsedArea ? 'right' : 'top') : 'bottom',
        },
        ButtonGroup: {
            classes: { rootHorizontal: actionsButtonGroupHorizontalClassName },
            gap: 'space1',
            orientation: isVertical && isCollapsedArea ? 'vertical' : 'horizontal',
        },
    }); }, [
        actionsButtonGroupHorizontalClassName,
        iconButtonDisabledClassName,
        iconButtonIconClassName,
        isCollapsedArea,
        isVertical,
    ]);
    var userMenuOverrideProps = React.useMemo(function () { return ({
        UserMenu: {
            classes: { caret: userMenuCaretClassName },
            avatarProps: { size: 'compact', variant: 'avatar-only' },
            position: isVertical ? 'right-start' : 'bottom-end',
        },
    }); }, [isVertical, userMenuCaretClassName]);
    return (_jsxs(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: [actions && (_jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: actionsOverrideProps }, { children: _jsx("div", __assign({ className: actionsContainerClassName }, { children: actions })) }))), children, userMenu && (_jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: userMenuOverrideProps }, { children: _jsx("div", __assign({ className: userMenuContainerClassName }, { children: userMenu })) })))] })));
});
NavigationUserControls.displayName = displayName;
